/**
 * Shared helpers for audit log component data sources (all / user / event-style rows).
 */

import { FacetFilterConfig } from '../../../../interfaces/DataSource/FacetFilterConfig.interface';

/** Maps UI filter keys to SQL columns understood by QueryFilter / audit-log API. */
const AUDIT_FILTER_SQL_COLUMNS: Record<string, string> = {
  change_type: 'al.change_type',
  entity_name: 'al.entity_name',
  field_name: 'al.field_name',
};

const AUDIT_CHANGE_TYPE_LABELS: Record<string, string> = {
  create: 'Oprettet',
  update: 'Opdateret',
  delete: 'Slettet',
};

const AUDIT_FIELD_NAME_LABELS: Record<string, string> = {
  participant_added: 'Deltager tilføjet',
  participant_removed: 'Deltager fjernet',
  status: 'Deltagerstatus',
  role: 'Deltagerrolle',
  data: 'Data / vedligeholdelsesminutter',
  event_created: 'Begivenhed oprettet',
  event_deleted: 'Begivenhed slettet',
  reminder_sent: 'Påmindelse sendt',
  event_type: 'Begivenhedstype',
  start_date: 'Starttidspunkt',
  end_date: 'Sluttidspunkt',
  visibility: 'Synlighed',
  catering: 'Catering',
  catering_extra_added: 'Forplejningskatalog: vare tilføjet',
  catering_extra_removed: 'Forplejningskatalog: vare fjernet',
  catering_extra_quantity: 'Forplejningskatalog: antal',
  catering_extra_comment: 'Forplejningskatalog: kommentar',
  catering_order_comment: 'Forplejningskatalog: ordrekommentar',
  catering_room_guid: 'Forplejningskatalog: lokale',
};

const AUDIT_ENTITY_NAME_LABELS: Record<string, string> = {
  duna_events: 'Begivenhed',
  duna_event_participants: 'Deltagelse',
  event_participant: 'Deltagelse',
  users: 'Bruger',
  notifications: 'Notifikation',
  duna_educational_paths: 'Uddannelsesforløb',
  duna_educational_path_users: 'Uddannelsesforløb (bruger)',
  contact_form: 'Kontaktformular',
};

export const AUDIT_FACET_FILTERS_ALL: FacetFilterConfig = {
  fields: [
    { key: 'entity_name', label: 'Type', sqlColumn: 'al.entity_name', labelMap: AUDIT_ENTITY_NAME_LABELS },
    { key: 'change_type', label: 'Handling', sqlColumn: 'al.change_type', labelMap: AUDIT_CHANGE_TYPE_LABELS },
    { key: 'field_name', label: 'Ændring', sqlColumn: 'al.field_name', labelMap: AUDIT_FIELD_NAME_LABELS },
  ],
};

export const AUDIT_FACET_FILTERS_EVENTS: FacetFilterConfig = {
  fields: [
    {
      key: 'entity_name',
      label: 'Type',
      sqlColumn: 'al.entity_name',
      labelMap: {
        duna_events: 'Begivenhed',
        duna_event_participants: 'Deltagelse',
        event_participant: 'Deltagelse',
      },
    },
    { key: 'change_type', label: 'Handling', sqlColumn: 'al.change_type', labelMap: AUDIT_CHANGE_TYPE_LABELS },
    { key: 'field_name', label: 'Ændring', sqlColumn: 'al.field_name', labelMap: AUDIT_FIELD_NAME_LABELS },
  ],
};

export function buildAuditListQueryParams(request: {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
  includeFilters?: boolean;
  includeFacets?: boolean;
  filters?: Record<string, any>;
}): Record<string, string> {
  const p: Record<string, string> = {
    page: String(request.page),
    pageSize: String(request.pageSize),
    // QueryFilter reads pageIndex (0-based), not page
    pageIndex: String(Math.max(0, request.page - 1)),
    _nocache: String(Date.now()),
  };
  if (request.sortBy) {
    p['sortBy'] = request.sortBy;
  }
  if (request.sortDir) {
    p['sortDir'] = request.sortDir;
  }
  if (request.search) {
    p['search'] = request.search;
    p['searchQuery'] = request.search;
  }
  if (request.includeFilters) {
    p['includeFilters'] = 'true';
  }
  if (request.includeFacets) {
    p['includeFacets'] = 'true';
  }
  if (request.filters) {
    const queryFilters: Array<{ column: string; value: string; operator: 'equals' }> = [];
    Object.entries(request.filters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      const sqlColumn = AUDIT_FILTER_SQL_COLUMNS[key];
      if (sqlColumn) {
        queryFilters.push({ column: sqlColumn, value: String(value), operator: 'equals' });
      }
    });
    if (queryFilters.length) {
      p['filters'] = JSON.stringify(queryFilters);
    }
  }
  return p;
}

export function normalizeAuditListResponse(
  result: any,
  fallbackPageSize: number
): { items: any[]; totalCount: number; pageSize: number; pageIndex: number; facets?: any } {
  if (result?.data && result?.meta) {
    return {
      items: result.data || [],
      totalCount: result.meta.totalItems ?? 0,
      pageSize: result.meta.pageSize ?? fallbackPageSize,
      pageIndex: result.meta.page != null ? result.meta.page - 1 : 0,
      facets: result.facets,
    };
  }
  if (result?.items) {
    return {
      items: result.items,
      totalCount: result.totalCount ?? 0,
      pageSize: result.pageSize ?? fallbackPageSize,
      pageIndex: result.pageIndex ?? 0,
      facets: result.facets,
    };
  }
  return { items: [], totalCount: 0, pageSize: fallbackPageSize, pageIndex: 0 };
}

function safeJson(value: any): Record<string, any> | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, any>;
  }
  try {
    const v = JSON.parse(String(value));
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

function fieldNorm(field: string): string {
  return String(field || '')
    .toLowerCase()
    .replace(/_/g, '');
}

function entityWhatLabelDa(entity: string): string {
  const e = String(entity || '').toLowerCase().trim();
  const map: Record<string, string> = {
    duna_events: 'Begivenhed',
    duna_event_participants: 'Deltagelse',
    event_participant: 'Deltagelse',
    duna_educational_paths: 'Uddannelsesforløb',
    duna_educational_path_users: 'Uddannelsesforløb (bruger)',
    users: 'Bruger',
    notifications: 'Notifikation',
  };
  return map[e] || (entity ? entity : 'Ukendt');
}

function fieldLabelDa(rawField: string): string {
  const f = fieldNorm(rawField);
  const map: Record<string, string> = {
    participantadded: 'Deltager tilføjet',
    participantremoved: 'Deltager fjernet',
    status: 'Deltagerstatus',
    role: 'Deltagerrolle',
    data: 'Data / vedligeholdelsesminutter',
    eventcreated: 'Begivenhed oprettet',
    eventdeleted: 'Begivenhed slettet',
  };
  return map[f] || rawField || '-';
}

function changeTypeLabelDa(changeType: string, fn: string): string {
  const ct = String(changeType || '').toLowerCase();
  if (fn === 'participantadded') {
    return 'Tilføjet';
  }
  if (fn === 'participantremoved') {
    return 'Fjernet';
  }
  const map: Record<string, string> = {
    create: 'Oprettet',
    update: 'Opdateret',
    delete: 'Slettet',
  };
  return map[ct] || changeType || '';
}

function truncateCell(value: string, max = 120): string {
  if (!value) {
    return '-';
  }
  return value.length > max ? value.substring(0, max) + '…' : value;
}

function translateParticipantRoleDa(role: string): string {
  const map: Record<string, string> = {
    attendee: 'Deltager',
    instructor: 'Instruktør',
    assistant: 'Assistent',
    organizer: 'Arrangør',
  };
  const key = String(role || '').toLowerCase().trim();
  return key ? map[key] || role : '-';
}

function translateParticipantStatusDa(status: string): string {
  const map: Record<string, string> = {
    pending: 'Afventer',
    confirmed: 'Bekræftet',
    waitlist: 'Venteliste',
    waitlisted: 'Venteliste',
    cancellation: 'Afmeldt',
    not_participant: 'Ikke deltager',
    completed: 'Gennemført',
    declined: 'Afslået',
    no_show: 'Udeblivelse',
  };
  const key = String(status || '').toLowerCase().trim();
  return key ? map[key] || status : '-';
}

function translateCommonAuditValueDa(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }
  const key = raw.toLowerCase();
  const boolMap: Record<string, string> = {
    '1': 'Ja',
    '0': 'Nej',
    true: 'Ja',
    false: 'Nej',
    yes: 'Ja',
    no: 'Nej',
  };
  if (boolMap[key]) {
    return boolMap[key];
  }
  const visibilityMap: Record<string, string> = {
    public: 'Offentlig',
    private: 'Privat',
    internal: 'Intern',
    organization: 'Organisation',
    visible: 'Synlig',
    hidden: 'Skjult',
  };
  if (visibilityMap[key]) {
    return visibilityMap[key];
  }
  const eventTypeMap: Record<string, string> = {
    course: 'Kursus',
    meeting: 'Møde',
    workshop: 'Workshop',
    conference: 'Konference',
    webinar: 'Webinar',
    other: 'Andet',
  };
  if (eventTypeMap[key]) {
    return eventTypeMap[key];
  }
  const roleOrStatus =
    translateParticipantRoleDa(raw) !== raw
      ? translateParticipantRoleDa(raw)
      : translateParticipantStatusDa(raw);
  if (roleOrStatus !== raw) {
    return roleOrStatus;
  }
  return raw;
}

function extractAffectedFromParticipantJson(
  fieldName: string,
  previousValue: any,
  newValue: any
): { name: string; email: string } {
  const fn = fieldNorm(fieldName);
  let decoded: Record<string, any> | null = null;
  if (fn === 'participantadded') {
    decoded = safeJson(newValue) || safeJson(previousValue);
  } else if (fn === 'participantremoved') {
    decoded = safeJson(previousValue) || safeJson(newValue);
  } else {
    decoded = safeJson(newValue) || safeJson(previousValue);
  }
  if (!decoded) {
    return { name: '', email: '' };
  }
  const pairs: [string, string][] = [
    ['first_name', 'last_name'],
    ['firstname', 'lastname'],
  ];
  for (const [a, b] of pairs) {
    const fnA = String(decoded[a] ?? '').trim();
    const ln = String(decoded[b] ?? '').trim();
    const full = `${fnA} ${ln}`.trim();
    if (full) {
      const email = String(decoded['email'] ?? decoded['user'] ?? '').trim();
      return { name: full, email };
    }
  }
  for (const k of ['full_name', 'displayName', 'display_name', 'name']) {
    const v = String(decoded[k] ?? '').trim();
    if (v) {
      return { name: v, email: '' };
    }
  }
  const uuid = String(
    decoded['user_uuid'] ?? decoded['userUuid'] ?? decoded['uuid'] ?? decoded['removed_user_uuid'] ?? ''
  ).trim();
  if (uuid) {
    return { name: `Bruger (${uuid})`, email: '' };
  }
  return { name: '', email: '' };
}

function localizeAuditDisplayValue(display: string | null | undefined): string {
  if (!display || display === '-') {
    return display || '-';
  }
  let result = display.replace(
    /\brolle:\s*([a-zA-Z_]+)/gi,
    (_match, role) => `rolle: ${translateParticipantRoleDa(role)}`
  );
  result = result.replace(
    /\bstatus:\s*([a-zA-Z_]+)/gi,
    (_match, status) => `status: ${translateParticipantStatusDa(status)}`
  );
  if (!result.includes(':') && !result.includes(',')) {
    return translateCommonAuditValueDa(result);
  }
  return result;
}

function formatParticipantPayloadSummary(decoded: Record<string, any> | null): string {
  if (!decoded) {
    return '-';
  }
  const bits: string[] = [];
  if (decoded['role']) {
    bits.push(`rolle: ${translateParticipantRoleDa(String(decoded['role']))}`);
  }
  if (decoded['status']) {
    bits.push(`status: ${translateParticipantStatusDa(String(decoded['status']))}`);
  }
  if (decoded['previous_role']) {
    bits.push(`rolle: ${translateParticipantRoleDa(String(decoded['previous_role']))}`);
  }
  if (decoded['previous_status']) {
    bits.push(`status: ${translateParticipantStatusDa(String(decoded['previous_status']))}`);
  }
  return bits.length ? bits.join(', ') : '-';
}

function formatValueCell(raw: any, fn: string, side: 'from' | 'to'): string {
  if (fn === 'participantadded') {
    const d = side === 'to' ? safeJson(raw) : null;
    return side === 'to' ? formatParticipantPayloadSummary(d) : '-';
  }
  if (fn === 'participantremoved') {
    const d = side === 'from' ? safeJson(raw) : null;
    return side === 'from' ? formatParticipantPayloadSummary(d) : '-';
  }
  if (raw == null || raw === '') {
    return '-';
  }
  if (typeof raw === 'object') {
    try {
      return truncateCell(JSON.stringify(raw));
    } catch {
      return '-';
    }
  }
  const s = translateCommonAuditValueDa(String(raw));
  return truncateCell(s, 200);
}

/** Full "Fra"/"Til" text in the details dialog (table cells may truncate). */
export function auditLogDetailValue(value: string | null | undefined): string {
  if (!value || value === '-') {
    return '-';
  }
  return value;
}

export const AUDIT_LOG_ROW_DETAIL_EXTRAS: Array<{
  label: string;
  value: (row: any) => string;
}> = [
  {
    label: 'Udført af (email)',
    value: (row) =>
      row.changed_by_email ||
      row.changed_by?.email ||
      row.changed_by?.mail ||
      '',
  },
  {
    label: 'Entitet (teknisk)',
    value: (row) => row.entity_name || '',
  },
  {
    label: 'Entitet ID',
    value: (row) =>
      row.entity_id != null && row.entity_id !== '' ? String(row.entity_id) : '',
  },
  {
    label: 'Felt (teknisk)',
    value: (row) => row.field_name || '',
  },
  {
    label: 'Log ID',
    value: (row) => (row.id != null ? String(row.id) : ''),
  },
];

export const AUDIT_LOG_DETAILS_ACTION_COLUMN = {
  key: 'actions',
  header: '',
  type: 'button' as const,
  buttonText: 'Detaljer',
  buttonColor: 'primary',
  alignment: 'right' as const,
  variant: 'stroked',
  opensRowDetailsModal: true,
};

export function mapAuditLogRowToDisplay(log: any): any {
  const fn = fieldNorm(log.field_name);
  const entity = String(log.entity_name || '');

  const changed_by_name =
    (log.changed_by && log.changed_by.name) || log.changed_by_name || 'Ukendt';
  const changed_by_email = log.changed_by_email || '';

  let what_label = log.what_label || entityWhatLabelDa(entity);
  let field_label = log.field_label || fieldLabelDa(log.field_name);
  let change_type_label =
    log.change_type_label || changeTypeLabelDa(String(log.change_type || ''), fn);
  let value_from_display = localizeAuditDisplayValue(
    log.value_from_display ?? formatValueCell(log.previous_value, fn, 'from')
  );
  let value_to_display = localizeAuditDisplayValue(
    log.value_to_display ?? formatValueCell(log.new_value, fn, 'to')
  );

  let affected_user_name = log.affected_user_name;
  let affected_user_email = log.affected_user_email || '';

  const isParticipantLike =
    entity.toLowerCase().includes('participant') ||
    fn === 'participantadded' ||
    fn === 'participantremoved';

  if ((!affected_user_name || affected_user_name === '-') && isParticipantLike) {
    const hint = extractAffectedFromParticipantJson(
      log.field_name,
      log.previous_value,
      log.new_value
    );
    if (hint.name) {
      affected_user_name = hint.name;
    }
    if (hint.email) {
      affected_user_email = hint.email;
    }
  }

  if ((!affected_user_name || affected_user_name === '-') && fn === 'remindersent') {
    const reminder = safeJson(log.new_value);
    if (reminder) {
      const fnPart = String(reminder['recipient_first_name'] ?? '').trim();
      const lnPart = String(reminder['recipient_last_name'] ?? '').trim();
      const full = String(reminder['recipient_name'] ?? `${fnPart} ${lnPart}`.trim()).trim();
      if (full) {
        affected_user_name = full;
      }
      const email = String(reminder['recipient_email'] ?? '').trim();
      if (email) {
        affected_user_email = email;
      }
    }
  }

  return {
    ...log,
    changed_by_name,
    changed_by_email,
    what_label,
    field_label,
    change_type_label,
    value_from_display,
    value_to_display,
    affected_user_name: affected_user_name && affected_user_name !== '-' ? affected_user_name : '-',
    affected_user_email,
  };
}
